import React from 'react';
import ReactDOM from 'react-dom';
import ReactModal from 'react-modal';
import { Provider } from 'react-redux';

import getTypedDataset from '@zr/utilities/dataset';

function createStore(storeCreator, initialState) {
  const store = storeCreator(initialState);
  const { dataset, app } = store.getState();
  let reducerKeyNotFound;

  if (!dataset && initialState.dataset) {
    reducerKeyNotFound = 'dataset';
  }

  if (!app && initialState.app) {
    reducerKeyNotFound = 'app';
  }

  if (reducerKeyNotFound) {
    throw new Error(`Expected to find the reducer key "${reducerKeyNotFound}" in the Redux store. `
      + 'Please use combineReducers from "@zr/utilities/redux" instead of "redux" when configuring your Redux store.');
  }

  return store;
}

/**
 * Renders a React component into the DOM in the supplied container.
 *
 * @param {React.Component} Component React component
 * @param {HTMLElement} container DOM element
 * @param {Object} [props] Additional props to add to the React component (and Redux store if storeCreator is provided)
 * @param {Function} [storeCreator] Function that creates a Redux store
 */
export default function render(Component, container, props = {}, storeCreator) {
  if (!container) {
    throw new Error('Container element is required.');
  }

  // Call this to properly hide your application from assistive screenreaders
  // and other assistive technologies while the modal is open.
  ReactModal.setAppElement(container);

  const dataset = getTypedDataset(container.dataset);

  let pageVariables = {};

  if (zr && zr.page && zr.page.model) {
    pageVariables = zr.page.model;
  }

  // create or clone React element, adding page variables and container dataset
  // <Component {...dataset} {...props} page={pageVariables} />
  let element = Component.$$typeof === Symbol.for('react.element')
    ? React.cloneElement(Component, { ...dataset, ...props, page: pageVariables })
    : React.createElement(Component, { ...dataset, ...props, page: pageVariables });

  if (storeCreator) {
    const initialState = {};
    // `dataset` and `props` should only be added to initial state if there are
    // matching reducers, otherwise Redux throws a "Unexpected key" error.
    // Those reducers are automatically included if the reducer that is provided
    // to the store creator uses @zr/utilities/redux/combineReducers

    if (Object.keys(dataset).length) { initialState.dataset = dataset; }

    // custom props are added to the Redux store under the 'app' reducer
    if (Object.keys(props).length) { initialState.app = props; }

    // add page variables under the 'page' reducer
    if (Object.keys(pageVariables).length) { initialState.page = pageVariables; }

    try {
      const store = createStore(storeCreator, initialState);

      // wrap component with Provider component
      // <Provider store={store}>
      //   <Component {...dataset} {...props}, page={pageVariables} />
      // </Provider>
      element = React.createElement(Provider, { store }, element);
    } catch (error) {
      console.error(error.message); // eslint-disable-line no-console
    }
  }

  ReactDOM.render(element, container);
}
